import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Box, Button, Card, CardContent, CardHeader, Stack, Typography, IconButton, Paper, Chip, Alert } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import WarningIcon from '@mui/icons-material/Warning'

import {
  useGetNetworkDesmosConfigQuery,
  useUpdateNetworkDesmosConfigMutation,
  useGetNetworkMembersQuery
} from '@/core/services/network'
import { useListWaitingRoomsQuery } from '@/core/services/waiting-room'
import { useGetActsQuery } from '@/core/services/acts'
import { DesmosActFormDialog } from '../components/DesmosActFormDialog'
import { NetworkDesmosAct } from '@/core/models/network.model'

export default function NetworkDesmosConfig() {
  const { t } = useTranslation()
  const { networkId } = useParams<{ networkId: string }>()
  const { enqueueSnackbar } = useSnackbar()

  const { data: config, isLoading, refetch } = useGetNetworkDesmosConfigQuery(networkId ?? '')
  const [updateConfig, { isLoading: isUpdating }] = useUpdateNetworkDesmosConfigMutation()

  // Fetch waiting rooms for the network
  const { data: waitingRoomsData } = useListWaitingRoomsQuery(
    { query: { network: networkId }, options: { limit: 0 } },
    { skip: !networkId }
  )

  // Fetch acts from admin portal for display
  const { data: allActs = [] } = useGetActsQuery({})

  // Fetch network members to derive establishments
  const { data: networkMembers = [] } = useGetNetworkMembersQuery(networkId ?? '', { skip: !networkId })

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [currentAct, setCurrentAct] = useState<NetworkDesmosAct | null>(null)
  const [acts, setActs] = useState<NetworkDesmosAct[]>([])
  const [isDirty, setIsDirty] = useState(false)

  useEffect(() => {
    if (config !== undefined) {
      setActs(config.waitingRoomActConfigs ?? [])
      setIsDirty(false)
    }
  }, [config])

  const handleAddAct = () => {
    setCurrentAct(null)
    setIsDialogOpen(true)
  }

  const handleEditAct = (act: NetworkDesmosAct) => {
    setCurrentAct(act)
    setIsDialogOpen(true)
  }

  const handleDeleteAct = (index: number) => {
    const newActs = [...acts]
    newActs.splice(index, 1)
    setActs(newActs)
    setIsDirty(true)
  }

  const handleSaveAct = (act: NetworkDesmosAct) => {
    if (currentAct) {
      // Update existing act
      const newActs = [...acts]
      const index = newActs.findIndex(a => a.actId === currentAct.actId)
      if (index !== -1) {
        newActs[index] = act
      }
      setActs(newActs)
    } else {
      // Add new act (check if not already added)
      const existingAct = acts.find(a => a.actId === act.actId)
      if (existingAct) {
        enqueueSnackbar(t('page.network.desmosConfig.actAlreadyConfigured'), { variant: 'warning' })
        return
      }
      setActs([...acts, act])
    }
    setIsDirty(true)
    setIsDialogOpen(false)
  }

  const handleSaveConfig = async () => {
    try {
      await updateConfig({
        networkId: networkId ?? '',
        body: { acts }
      }).unwrap()

      setIsDirty(false)
      enqueueSnackbar(t('page.network.desmosConfig.updateSuccess'), { variant: 'success' })
    } catch (error) {
      console.error('Error updating Desmos config:', error)
      enqueueSnackbar(t('page.network.desmosConfig.updateError'), { variant: 'error' })
    }
  }

  const getActDetails = (actId: string) => {
    return allActs.find((a: any) => a._id === actId)
  }

  const getWaitingRoomNames = (waitingRoomIds?: string[]) => {
    if (!waitingRoomIds || waitingRoomIds.length === 0) {
      return t('page.network.desmosConfig.allWaitingRooms')
    }
    return waitingRoomIds
      .map(id => waitingRoomsData?.items?.find(wr => wr._id === id)?.name || id)
      .join(', ')
  }

  // Derive unique establishments from network members
  const establishments = networkMembers.reduce((acc, member) => {
    const est = (member.member as any)?.establishment
    if (est?._id && !acc.some(e => e._id === est._id)) {
      acc.push({ _id: est._id, name: est.name || est._id })
    }
    return acc
  }, [] as { _id: string; name: string }[])

  const getEstablishmentName = (establishmentId?: string) => {
    if (!establishmentId) return null
    const est = establishments.find(e => e._id === establishmentId)
    return est?.name || establishmentId
  }

  const hasLegacyActs = acts.some(act => !act.establishmentId)

  return (
    <Box>
      <Card>
        <CardHeader
          title={t('page.network.desmosConfig.title')}
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddAct}
            >
              {t('page.network.desmosConfig.addAct')}
            </Button>
          }
        />
        <CardContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('page.network.desmosConfig.description')}
          </Typography>

          {hasLegacyActs && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
              {t('page.network.desmosConfig.legacyActsWarning', {
                defaultValue: 'Some acts do not have an establishment configured. Please edit them to assign an establishment, otherwise they will not be auto-created.'
              })}
            </Alert>
          )}

          <Stack spacing={2}>
            {acts.map((act, index) => {
              const actDetails = getActDetails(act.actId)
              return (
                <Paper
                  key={act.actId}
                  variant="outlined"
                  sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">
                      {actDetails?.libelle || `Act ID: ${act.actId}`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {act.autoCreate
                        ? t('page.network.desmosConfig.autoCreate.yes')
                        : t('page.network.desmosConfig.autoCreate.no')}
                      {actDetails?.honoraire && `, ${t('common.honorarium')}: ${actDetails.honoraire}€`}
                      {actDetails?.id_transposition && `, ${t('common.code')}: ${actDetails.id_transposition}`}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {/* Establishment chip */}
                      {act.establishmentId ? (
                        <Chip
                          label={getEstablishmentName(act.establishmentId)}
                          size="small"
                          color="secondary"
                          variant="filled"
                        />
                      ) : (
                        <Chip
                          icon={<WarningIcon />}
                          label={t('page.network.desmosConfig.noEstablishment', { defaultValue: 'No establishment' })}
                          size="small"
                          color="warning"
                        />
                      )}
                      {/* Waiting room chips */}
                      {(!act.waitingRooms || act.waitingRooms.length === 0) ? (
                        <Chip label={t('page.network.desmosConfig.allWaitingRooms')} size="small" color="primary" variant="outlined" />
                      ) : (
                        act.waitingRooms.map(wrId => {
                          const room = waitingRoomsData?.items?.find(wr => wr._id === wrId)
                          return (
                            <Chip
                              key={wrId}
                              label={room?.name || wrId}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )
                        })
                      )}
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditAct(act)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteAct(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Paper>
              )
            })}

            {acts.length === 0 && (
              <Typography variant="body1" color="text.secondary" align="center" sx={{ py: 4 }}>
                {t('page.network.desmosConfig.noActs')}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleSaveConfig}
              disabled={isUpdating || !isDirty}
            >
              {t('page.network.desmosConfig.saveConfiguration')}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <DesmosActFormDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSave={handleSaveAct}
        initialAct={currentAct}
        waitingRooms={waitingRoomsData?.items?.map(wr => ({ _id: wr._id, name: wr.name })) || []}
        establishments={establishments}
      />
    </Box>
  )
}